import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useUser } from '../auth/UserContext';
import { ActionModal } from '../components/ActionModals';
import { UserSearch } from '../components/UserSearch';
import { downloadScoreReport } from '../utils/scoreReport';
import * as api from '../api/client';

interface FactorResult {
  key: string;
  label: string;
  numerator: number;
  denominator: number;
  ratio: number;
  items: WorklistItem[];
}

interface WorklistItem {
  id: string;
  recordId: string;
  recordType: string;
  name: string;
  client: string;
  date: string;
  issueType: string;
  issueLabel: string;
  actions: string[];
  acknowledged: boolean;
  email?: string;
  personalEmail?: string;
  phone?: string;
  hasSource?: boolean;
  hasSectorTag?: boolean;
  hasCity?: boolean;
  hasCountry?: boolean;
}

interface ScoreData {
  locked: boolean;
  userId: string;
  name: string;
  overall: number;
  factors: FactorResult[];
  flaggedVacancies28Day: WorklistItem[];
  totalIssues: number;
  acknowledgedCount: number;
  needsUserSelection?: boolean;
  users?: { id: string; fullName: string; email: string; territory: string }[];
}

const ISSUE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'missing-cv', label: 'Missing CV' },
  { key: 'source-sector', label: 'Source / Sector' },
  { key: 'ad-response', label: 'Ad-response' },
  { key: '28-day-flag', label: '28-day flag' },
  { key: 'currency-mismatch', label: 'Currency' },
  { key: 'missing-country', label: 'Country' },
  { key: 'missing-solution', label: 'Solution' },
  { key: 'stale-vacancy', label: 'Stale vacancy' },
];

const ACTION_LABELS: Record<string, string> = {
  'log-call': 'Log call',
  'add-tags': 'Add tags',
  'close-vacancy': 'Close vacancy',
  'still-open': 'Still open',
  'fix-currency': 'Fix currency',
  'correct-as-is': 'Correct as-is',
  'fix-address': 'Fix address',
  'fix-solution': 'Fix solution',
  'edit-contact': 'Edit contact',
  'upload-cv': 'Upload CV',
};

function scoreColor(ratio: number): string {
  if (ratio >= 1.0) return 'var(--state-positive)';
  if (ratio >= 0.75) return 'var(--text-primary)';
  return 'var(--state-warning)';
}

function progressClass(ratio: number): string {
  if (ratio >= 1.0) return 'progress-fill-sage';
  if (ratio >= 0.75) return 'progress-fill-teal';
  return 'progress-fill-warning';
}

// Must mirror FACTOR_WEIGHTS in api/src/lib/scoreEngine.ts. The overall score
// is recomputed here after an action for instant feedback, so if these drift
// apart the ring would disagree with the backend on the next reload.
const FACTOR_WEIGHTS: Record<string, number> = {
  candidatesVsTarget: 0.5,
};

function factorWeight(key: string): number {
  return FACTOR_WEIGHTS[key] ?? 1;
}

function pillClass(count: number): string {
  if (count === 0) return 'pill pill-sage';
  if (count <= 3) return 'pill pill-teal';
  return 'pill pill-warning';
}

export function ConsultantView() {
  const { account } = useAuth();
  const { user: currentUser } = useUser();
  const [searchParams] = useSearchParams();
  const urlUserId = searchParams.get('userId');

  // Only allow viewing others' scores if team_lead, director, or admin
  const canViewOthers = currentUser && ['team_lead', 'director', 'admin'].includes(currentUser.role);
  const targetUserId = canViewOthers ? urlUserId : null;

  const [score, setScore] = useState<ScoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeAction, setActiveAction] = useState<{ action: string; item: any } | null>(null);
  const [viewingName, setViewingName] = useState('');

  const loadScore = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.getScore(targetUserId || undefined);
      setScore(result);
      if (result.name) setViewingName(result.name);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    loadScore();
  }, [loadScore]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Loading score...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '480px', margin: '40px auto', textAlign: 'center' }}>
        <div className="warning-banner" style={{ marginBottom: '16px' }}>{error}</div>
        <button className="btn btn-primary" onClick={loadScore}>Retry</button>
      </div>
    );
  }

  if (!score) return null;

  // Admin landing page — org snapshot + user picker
  if (score.needsUserSelection && score.users) {
    return (
      <div>
        <h1 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '24px' }}>Dashboard</h1>

        <UserSearch />

        {/* User picker */}
        <div className="card" style={{ maxWidth: '600px' }}>
          <div className="card-header">Select a consultant to view</div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '8px 0 16px' }}>
            As admin, you can view any consultant's score. Pick one below.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {score.users.map(u => (
              <button
                key={u.id}
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start', padding: '10px 16px' }}
                onClick={() => window.location.href = `/?userId=${u.id}`}
              >
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{u.fullName}</span>
                <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--text-secondary)' }}>{u.territory}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Combine all worklist items
  const allItems = [
    ...(score.factors || []).flatMap(f => f.items),
    ...(score.flaggedVacancies28Day || []),
  ];

  const filteredItems = activeFilter === 'all'
    ? allItems
    : allItems.filter(i => i.issueType === activeFilter);

  const activeItems = filteredItems.filter(i => !i.acknowledged);
  const acknowledgedItems = filteredItems.filter(i => i.acknowledged);

  return (
    <div>
      {/* Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '16px', fontWeight: 500 }}>
          {targetUserId ? `Dashboard — ${viewingName}` : 'Dashboard'}
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {currentUser?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={() => downloadScoreReport(score)}>
              ⬇ Download PDF
            </button>
          )}
          {targetUserId && (
            <button className="btn btn-secondary" onClick={() => window.location.href = '/'}>
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Admin-only: quick jump to any consultant's score.
          The company snapshot now lives on its own /company page so the
          dashboard doesn't wait on that heavy live-scoring call. */}
      {currentUser?.role === 'admin' && <UserSearch />}

      {/* Score ring + factor cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', marginBottom: '24px' }}>
        {/* Score ring */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div className="card-header">Overall Score</div>
          <div
            className="score-ring"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              border: `4px solid ${scoreColor(score.overall)}`,
              margin: '16px 0',
            }}
          >
            <span className="score-ring-value" style={{ color: scoreColor(score.overall) }}>
              {Math.round(score.overall * 100)}%
            </span>
          </div>
          <span className={pillClass(score.totalIssues)}>
            {score.totalIssues} issue{score.totalIssues !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Factor cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {(score.factors || []).map(f => (
            <div key={f.key} className="card">
              <div className="card-header">{f.label}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', margin: '8px 0' }}>
                <span className="card-number" style={{ color: scoreColor(f.ratio) }}>
                  {Math.round(f.ratio * 100)}%
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {f.numerator} of {f.denominator}
                </span>
              </div>
              <div className="progress-track">
                <div
                  className={`progress-fill ${progressClass(f.ratio)}`}
                  style={{ width: `${Math.round(f.ratio * 100)}%` }}
                />
              </div>
              {f.items.length > 0 && (
                <span className={pillClass(f.items.length)} style={{ marginTop: '8px' }}>
                  {f.items.length} issue{f.items.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 28-day flag banner */}
      {(score.flaggedVacancies28Day || []).length > 0 && (
        <div className="warning-banner" style={{ marginBottom: '20px' }}>
          ⚠ {score.flaggedVacancies28Day.length} vacanc{score.flaggedVacancies28Day.length === 1 ? 'y has' : 'ies have'} no shortlist activity in the last 28 days
        </div>
      )}

      {/* Worklist */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div className="card-header" style={{ marginBottom: 0 }}>Worklist</div>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {allItems.length} total, {score.acknowledgedCount} acknowledged
          </span>
        </div>

        {/* Filter tabs */}
        <div className="month-tabs" style={{ marginBottom: '16px', flexWrap: 'wrap' }}>
          {ISSUE_FILTERS.map(filter => {
            const count = filter.key === 'all'
              ? allItems.length
              : allItems.filter(i => i.issueType === filter.key).length;
            if (count === 0 && filter.key !== 'all') return null;
            return (
              <button
                key={filter.key}
                className={`month-tab${activeFilter === filter.key ? ' active' : ''}`}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.label}
                <span style={{ marginLeft: '4px', opacity: 0.6 }}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Active items */}
        {activeItems.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Client / Vacancy</th>
                <th>Issue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map(item => (
                <tr key={item.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{item.name}</div>
                    {item.issueType === 'missing-cv' && (
                      // Both email slots, labelled. DaXtra matches an uploaded CV on
                      // the personal address, so a consultant needs to see whether
                      // it's on the record before uploading — otherwise the CV lands
                      // on a new duplicate contact.
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
                        <span>
                          Personal:{' '}
                          {item.personalEmail
                            ? item.personalEmail
                            : <span style={{ color: 'var(--state-warning)', fontWeight: 500 }}>missing</span>}
                        </span>
                        {item.email && <span>Work: {item.email}</span>}
                        {item.phone && <span>{item.phone}</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{item.client}</td>
                  <td>
                    <span className="pill pill-warning">{item.issueLabel}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {item.actions.map(action => (
                        <button
                          key={action}
                          className={`btn ${action === item.actions[0] ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ padding: '4px 10px', fontSize: '11px' }}
                          onClick={() => setActiveAction({ action, item })}
                        >
                          {ACTION_LABELS[action] || action}
                        </button>
                      ))}
                      {currentUser?.role === 'admin' && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '11px', color: 'var(--state-warning)' }}
                          onClick={() => setActiveAction({ action: 'dismiss', item })}
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
            {allItems.length === 0 ? 'No issues — perfect score!' : 'No items match this filter'}
          </p>
        )}

        {/* Acknowledged items */}
        {acknowledgedItems.length > 0 && (
          <>
            <div style={{ margin: '16px 0 8px', fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Acknowledged ({acknowledgedItems.length})
            </div>
            <table className="data-table">
              <tbody>
                {acknowledgedItems.map(item => (
                  <tr key={item.id} style={{ opacity: 0.5 }}>
                    <td>{item.name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{item.client}</td>
                    <td>
                      <span className="pill pill-sage">{item.issueLabel}</span>
                    </td>
                    <td style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Acknowledged</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Action modal */}
      {activeAction && (
        <ActionModal
          action={activeAction.action}
          item={activeAction.item}
          consultantId={score?.userId || ''}
          onClose={() => setActiveAction(null)}
          onComplete={() => {
            const item = activeAction.item;
            const action = activeAction.action;
            setActiveAction(null);

            if (!score) return;

            // Update local state instead of reloading
            if (action === 'still-open' || action === 'correct-as-is') {
              // Acknowledging counts the item as passing (same as the backend),
              // so lift the factor's ratio and the overall score — not just the
              // issue count. (28-day flags aren't a scored factor, so those only
              // clear from the warning list without moving the ring.)
              setScore(prev => {
                if (!prev) return prev;
                const newFactors = prev.factors.map(f => {
                  const affects = f.items.some(i => i.id === item.id && !i.acknowledged);
                  if (!affects) return f;
                  const newItems = f.items.map(i => i.id === item.id ? { ...i, acknowledged: true } : i);
                  const newNumerator = f.numerator + 1;
                  const newRatio = f.denominator === 0 ? 1.0 : Math.min(newNumerator / f.denominator, 1.0);
                  return { ...f, items: newItems, numerator: newNumerator, ratio: newRatio };
                });
                const newFlags = prev.flaggedVacancies28Day.map(i => i.id === item.id ? { ...i, acknowledged: true } : i);
                const overall = newFactors.reduce((p, f) => p * Math.pow(f.ratio, factorWeight(f.key)), 1.0);
                const allItems = [...newFactors.flatMap(f => f.items), ...newFlags];
                return {
                  ...prev,
                  factors: newFactors,
                  flaggedVacancies28Day: newFlags,
                  overall,
                  totalIssues: allItems.filter(i => !i.acknowledged).length,
                  acknowledgedCount: allItems.filter(i => i.acknowledged).length,
                };
              });
            } else {
              // Remove the item entirely (it's been resolved)
              setScore(prev => {
                if (!prev) return prev;
                const removeItem = (items: WorklistItem[]) => items.filter(i => i.id !== item.id);
                const newFactors = prev.factors.map(f => {
                  const newItems = removeItem(f.items);
                  const removedCount = f.items.length - newItems.length;
                  if (removedCount === 0) return f;
                  const newNumerator = f.numerator + removedCount;
                  const newRatio = f.denominator === 0 ? 1.0 : Math.min(newNumerator / f.denominator, 1.0);
                  return { ...f, items: newItems, numerator: newNumerator, ratio: newRatio };
                });
                const newFlags = removeItem(prev.flaggedVacancies28Day);
                const overall = newFactors.reduce((p, f) => p * Math.pow(f.ratio, factorWeight(f.key)), 1.0);
                const allItems = [...newFactors.flatMap(f => f.items), ...newFlags];
                return {
                  ...prev,
                  factors: newFactors,
                  flaggedVacancies28Day: newFlags,
                  overall,
                  totalIssues: allItems.filter(i => !i.acknowledged).length,
                  acknowledgedCount: allItems.filter(i => i.acknowledged).length,
                };
              });
            }
          }}
        />
      )}
    </div>
  );
}
